'use client';

import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import type { OperationStatus } from '../types';

interface ExecutionProgressProps {
  operationStatus?: OperationStatus | null;
}

export function ExecutionProgress({ operationStatus }: ExecutionProgressProps) {
  const progress = operationStatus?.progress ?? 0;
  const isRunning = operationStatus?.status === 'running';
  const isPending = operationStatus?.status === 'pending' || !operationStatus;

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />

        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold">
            {isPending ? 'Starting operation...' : 'Processing subscriptions...'}
          </h3>
          <p className="text-sm text-muted-foreground">
            Please wait while the operation is being executed. This may take a few minutes.
          </p>
        </div>

        <div className="w-full max-w-md space-y-2">
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{progress}% complete</span>
            {operationStatus && (
              <span>
                {operationStatus.processedCount} / {operationStatus.targetCount} processed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats cards */}
      {operationStatus && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Processed
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">
                {operationStatus.processedCount.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium text-green-700">Success</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-green-700">
                {operationStatus.successCount.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card className={operationStatus.failedCount > 0 ? 'border-red-200 bg-red-50/50' : ''}>
            <CardHeader className="py-3">
              <CardTitle className={`text-sm font-medium ${operationStatus.failedCount > 0 ? 'text-red-700' : 'text-muted-foreground'}`}>
                Failed
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className={`text-2xl font-bold ${operationStatus.failedCount > 0 ? 'text-red-700' : ''}`}>
                {operationStatus.failedCount.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
